import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useEnvironmentPortPollingInterval } from "./useEnvironmentPortPolling";
import { createConnectClient } from "server/connect";
import type { Environment } from "components/Environment/utils/types";
import {
  HttpPortProtocol,
  NetworkMemberType,
  PortProtocol,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";
import {
  EnvironmentServiceSetHTTPPortAccessRequestAuthorizedEmails,
  EnvironmentServiceSetPortAllowedSourcesRequestAllowedSources,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import { EnvironmentService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/environment_connect";
import {
  getLegacyFirewallPorts,
  getLegacySecureLinkPorts,
  isLegacyNetworkAccessPort,
  toDisplayPorts,
  type DisplayPort,
} from "utils/environment/networkAccessFallback";
import { buildSecureLinkCustomHostname } from "components/Environment/utils/secureLinkHostname";
import { getEnvironmentNetworkInfoQueryKey } from "components/Environment/networkInfo";

const client = createConnectClient(EnvironmentService);
const EMPTY_DISPLAY_PORTS: DisplayPort[] = [];

export interface OpenEnvironmentHTTPPortOptions {
  httpProtocol: HttpPortProtocol;
  customHostname?: string;
  authorizedEmails: string[];
  allowPublicUnauthenticated: boolean;
}

function splitPorts(ports: DisplayPort[]) {
  const httpPorts = ports.filter(
    (port) =>
      (port.httpProtocol ?? HttpPortProtocol.UNSPECIFIED) !==
      HttpPortProtocol.UNSPECIFIED,
  );
  const tcpPorts = ports.filter(
    (port) =>
      (port.httpProtocol ?? HttpPortProtocol.UNSPECIFIED) ===
      HttpPortProtocol.UNSPECIFIED,
  );
  return { httpPorts, tcpPorts };
}

function shouldLoadNetworkInfo(environment: Environment | null): boolean {
  return Boolean(
    environment?.instance?.instance_id &&
    environment.instance?.status?.object_status !== "pending",
  );
}

type CtaApplication = {
  hostname?: string;
  name: string;
  port?: number | string;
};

const LEGACY_BREVLAB_HOSTNAME_SUFFIX = ".brevlab.com";

const stringPort = (port: number | string | undefined): string | undefined =>
  port == null ? undefined : String(port);

const hostnameName = (hostname: string | undefined): string =>
  hostname?.split(".")[0] ?? "";

const stripKnownSuffix = (
  value: string,
  appIDCandidates: Array<string | undefined>,
): string => {
  const ids = appIDCandidates.filter(
    (id): id is string => typeof id === "string" && id.length > 0,
  );
  const matchingID = ids.find((id) => value.endsWith(`-${id}`));
  return matchingID ? value.slice(0, -matchingID.length - 1) : value;
};

const isReadySecureLink = (
  port: Pick<DisplayPort, "hostname" | "isEndpointPending">,
) => Boolean(port.hostname && !port.isEndpointPending);

function findSecureLinkForCta(
  app: CtaApplication,
  secureLinkHTTPPorts: readonly DisplayPort[],
  appIDCandidates: Array<string | undefined>,
): DisplayPort | undefined {
  const readySecureLinks = secureLinkHTTPPorts.filter(isReadySecureLink);
  const appPort = stringPort(app.port);
  if (appPort) {
    const serverPortMatch = readySecureLinks.find(
      (port) => stringPort(port.serverPort) === appPort,
    );
    if (serverPortMatch) return serverPortMatch;

    const publicPortMatch = readySecureLinks.find(
      (port) => stringPort(port.portNumber) === appPort,
    );
    if (publicPortMatch) return publicPortMatch;
  }

  const appNames = [app.name, hostnameName(app.hostname)]
    .map((name) => stripKnownSuffix(name, appIDCandidates))
    .filter(Boolean);

  return readySecureLinks.find((port) => {
    const secureLinkName = stripKnownSuffix(
      hostnameName(port.hostname),
      appIDCandidates,
    );
    return appNames.includes(secureLinkName);
  });
}

function resolveSecureLinkCtaHostname({
  app,
  secureLinkHTTPPorts,
  isSkybridge,
  appIDCandidates,
}: {
  app: CtaApplication;
  secureLinkHTTPPorts: readonly DisplayPort[];
  isSkybridge: boolean;
  appIDCandidates: Array<string | undefined>;
}): string | undefined {
  if (!isSkybridge) return app.hostname;

  const secureLink = findSecureLinkForCta(
    app,
    secureLinkHTTPPorts,
    appIDCandidates,
  );
  if (secureLink?.hostname) return secureLink.hostname;

  return app.hostname?.endsWith(LEGACY_BREVLAB_HOSTNAME_SUFFIX)
    ? undefined
    : app.hostname;
}

export function useEnvironmentNetworkAccess(environment: Environment | null) {
  const environmentID = environment?.environment_id || "";
  const isSkybridge = environment?.labels?.["tunnel-provider"] === "skybridge";
  const canLoadNetworkInfo = shouldLoadNetworkInfo(environment);
  const portPollingInterval = useEnvironmentPortPollingInterval(environment);
  const networkInfoQueryKey = useMemo(
    () => getEnvironmentNetworkInfoQueryKey(environmentID),
    [environmentID],
  );
  const legacyHTTPPorts = useMemo(
    () => getLegacySecureLinkPorts(environment),
    [environment],
  );
  const legacyTCPPorts = useMemo(
    () => getLegacyFirewallPorts(environment),
    [environment],
  );

  const {
    data: networkAccess,
    refetch: refetchNetworkInfo,
    isInitialLoading: isNetworkInfoInitialLoading,
    isError: isNetworkInfoError,
  } = useQuery({
    queryKey: networkInfoQueryKey,
    queryFn: ({ signal }) =>
      client.getNetworkInfo({ environmentId: environmentID }, { signal }),
    select: (response) => {
      const ports = toDisplayPorts(response.networkInfo?.ports ?? []);
      const split = splitPorts(ports);
      return {
        info: response.networkInfo,
        ports,
        httpPorts: split.httpPorts,
        tcpPorts: split.tcpPorts,
      };
    },
    enabled: canLoadNetworkInfo,
    retry: false,
    refetchInterval: portPollingInterval,
    refetchIntervalInBackground: false,
    onError: (error) => {
      console.error("Failed to load environment network info", error);
    },
  });

  const environmentNetworkInfo = networkAccess?.info;
  const networkHTTPPorts = networkAccess?.httpPorts ?? EMPTY_DISPLAY_PORTS;
  const networkTCPPorts = networkAccess?.tcpPorts ?? EMPTY_DISPLAY_PORTS;

  const hasNetworkPorts =
    networkHTTPPorts.length > 0 || networkTCPPorts.length > 0;
  const httpPorts = hasNetworkPorts ? networkHTTPPorts : legacyHTTPPorts;
  const tcpPorts = hasNetworkPorts ? networkTCPPorts : legacyTCPPorts;
  const resolveCtaHostname = useCallback(
    (app: CtaApplication) =>
      resolveSecureLinkCtaHostname({
        app,
        secureLinkHTTPPorts: networkHTTPPorts,
        isSkybridge,
        appIDCandidates: [environment?.workspaceId, environmentID],
      }),
    [environment?.workspaceId, environmentID, networkHTTPPorts, isSkybridge],
  );
  const [shouldShowInitialNetworkInfoLoading] = useState(canLoadNetworkInfo);
  const isLoadingPorts =
    shouldShowInitialNetworkInfoLoading && isNetworkInfoInitialLoading;

  const refetchNetworkInfoAfterMutation = useCallback(async () => {
    if (!canLoadNetworkInfo) return;
    await refetchNetworkInfo();
  }, [canLoadNetworkInfo, refetchNetworkInfo]);

  const openPort = useCallback(
    async (
      portNumber: number,
      protocol: PortProtocol,
      allowedSources: string[],
      toPortNumber?: number,
    ) => {
      if (!environmentID) return;
      if (toPortNumber === undefined) {
        await client.openPort({
          environmentId: environmentID,
          portNumber,
          protocol,
          allowedSources,
        });
      } else {
        await client.openSequentialPorts({
          environmentId: environmentID,
          fromPortNumber: portNumber,
          toPortNumber,
          protocol,
          allowedSources,
        });
      }
      await refetchNetworkInfoAfterMutation();
    },
    [environmentID, refetchNetworkInfoAfterMutation],
  );

  const openHTTPPort = useCallback(
    async (portNumber: number, options: OpenEnvironmentHTTPPortOptions) => {
      if (!environmentID) return;
      await client.openHTTPPort({
        environmentId: environmentID,
        portNumber,
        httpProtocol: options.httpProtocol,
        customHostname: buildSecureLinkCustomHostname(
          options.customHostname,
          portNumber,
          environmentID,
        ),
        authorizedEmails: options.authorizedEmails,
        allowPublicUnauthenticated: options.allowPublicUnauthenticated,
      });
      await refetchNetworkInfoAfterMutation();
    },
    [environmentID, refetchNetworkInfoAfterMutation],
  );

  const setPortTarget = useCallback(
    async (portId: string, targetPort: number) => {
      await client.setPortTarget({
        portId,
        portNumber: targetPort,
      });
      await refetchNetworkInfoAfterMutation();
    },
    [refetchNetworkInfoAfterMutation],
  );

  const setPortAllowedSources = useCallback(
    async (portId: string, allowedSources: string[]) => {
      await client.setPortAllowedSources({
        portId,
        allowedSources:
          new EnvironmentServiceSetPortAllowedSourcesRequestAllowedSources({
            cidrBlocks: allowedSources,
          }),
      });
      await refetchNetworkInfoAfterMutation();
    },
    [refetchNetworkInfoAfterMutation],
  );

  const setHTTPPortProtocol = useCallback(
    async (portId: string, httpProtocol: HttpPortProtocol) => {
      await client.setHTTPPortProtocol({ portId, httpProtocol });
      await refetchNetworkInfoAfterMutation();
    },
    [refetchNetworkInfoAfterMutation],
  );

  const setHTTPPortAccess = useCallback(
    async (
      portId: string,
      authorizedEmails: string[],
      allowPublicUnauthenticated: boolean,
    ) => {
      await client.setHTTPPortAccess({
        portId,
        authorizedEmails:
          new EnvironmentServiceSetHTTPPortAccessRequestAuthorizedEmails({
            emails: authorizedEmails,
          }),
        allowPublicUnauthenticated,
      });
      await refetchNetworkInfoAfterMutation();
    },
    [refetchNetworkInfoAfterMutation],
  );

  const closePort = useCallback(
    async (portId: string) => {
      await client.closePort({ portId });
      await refetchNetworkInfoAfterMutation();
    },
    [refetchNetworkInfoAfterMutation],
  );

  const setNetworkMemberType = useCallback(
    async (memberType: NetworkMemberType) => {
      if (!environmentID) return;
      await client.setNetworkMemberType({
        environmentId: environmentID,
        networkMemberType: memberType,
      });
      await refetchNetworkInfoAfterMutation();
    },
    [environmentID, refetchNetworkInfoAfterMutation],
  );

  return {
    httpPorts,
    tcpPorts,
    isLoadingPorts,
    isLoadingNetworkInfo: canLoadNetworkInfo && isNetworkInfoInitialLoading,
    isNetworkInfoError,
    environmentNetworkInfo,
    openPort,
    openHTTPPort,
    setPortTarget,
    setPortAllowedSources,
    setHTTPPortProtocol,
    setHTTPPortAccess,
    closePort,
    setNetworkMemberType,
    resolveCtaHostname,
    canManagePort: (port: DisplayPort) => !isLegacyNetworkAccessPort(port),
  };
}
